import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BadgeCheck, Check, FileCheck2, Lock, MailCheck } from "lucide-react";
import { api } from "../lib/api";
import { Brand } from "../components/Shell";
import { Button } from "../components/ui/button";

const HERO = `${process.env.PUBLIC_URL || ""}/confirmterms-flow.png`;

const FEATURES = [
  {
    icon: MailCheck,
    title: "References answer in one tap",
    body: "Each trade reference gets a secure one-tap link by email — no account, no code to type.",
  },
  {
    icon: FileCheck2,
    title: "One packet, ready to file",
    body: "Download every completed reference plus your W-9 as a single branded PDF.",
  },
  {
    icon: BadgeCheck,
    title: "A registry that pays forward",
    body: "References that opt in keep a reusable profile, so repeat requests are answered instantly.",
  },
  {
    icon: Lock,
    title: "Chased automatically",
    body: "Silent references are followed up for you every few days until they reply.",
  },
];

export default function Landing() {
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    api
      .get("/plans")
      .then(({ data }) => setPlans(data))
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-6">
          <Brand />
          <div className="flex items-center gap-2 sm:gap-3">
            <a
              href="#pricing"
              className="hidden text-sm font-semibold text-slate-600 transition-colors duration-200 hover:text-slate-900 sm:block"
            >
              Pricing
            </a>
            <Link to="/signup">
              <Button
                data-testid="landing-signup-button"
                className="h-10 rounded-full bg-blue-600 px-4 text-sm font-semibold transition-colors duration-200 hover:bg-blue-700 active:scale-95 sm:px-6"
              >
                Free account
              </Button>
            </Link>
            <Link to="/login" className="hidden sm:block">
              <Button
                variant="outline"
                data-testid="landing-signin-button"
                className="h-10 rounded-full border-slate-300 px-4 text-sm font-semibold transition-colors duration-200 hover:bg-slate-100 sm:px-6"
              >
                Sign in
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden bg-[#0d1b3e]">
        <div className="relative mx-auto max-w-6xl px-6 py-20 lg:py-24">
          <div className="ct-rise max-w-3xl">
            <div className="text-xs font-semibold uppercase tracking-[0.24em] text-blue-300">
              ConfirmTerms.com
            </div>
            <h1 className="mt-5 font-display text-4xl font-black leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
              Know who you are
              <br />
              extending credit to.
            </h1>
            <p className="mt-6 max-w-2xl text-base text-slate-300 md:text-lg">
              A complete workflow that manages the entire credit approval process — ConfirmTerms
              collects and verifies trade references for you, so you can approve terms with
              confidence instead of guesswork.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/signup">
                <Button
                  data-testid="landing-hero-cta"
                  className="h-12 rounded-full bg-blue-600 px-8 text-[15px] font-semibold transition-colors duration-200 hover:bg-blue-700 active:scale-95"
                >
                  Create free account <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
                </Button>
              </Link>
              <Link to="/login">
                <Button
                  variant="outline"
                  className="h-12 rounded-full border-white/30 bg-white/10 px-8 text-[15px] font-semibold text-white transition-colors duration-200 hover:bg-white/20"
                >
                  Sign in to your portal
                </Button>
              </Link>
            </div>
          </div>

          <div className="ct-rise mt-14 rounded-2xl bg-white p-4 shadow-2xl sm:p-7">
            <img
              src={HERO}
              alt="A complete workflow that manages the entire credit approval process: your Vendor/Supplier subscribes, your customer receives a secure link to start their credit application, and each trade reference is emailed a request"
              data-testid="landing-hero-image"
              className="w-full rounded-lg"
            />
          </div>
        </div>
      </section>


      <section className="border-y border-slate-200 bg-slate-50 py-16 lg:py-20">
        <div className="mx-auto grid max-w-6xl gap-8 px-6 sm:grid-cols-2">
          {FEATURES.map((f) => (
            <div
              key={f.title}
              className="rounded-xl border border-slate-200 bg-white p-7 shadow-sm transition-colors duration-200 hover:border-blue-300"
            >
              <f.icon className="h-6 w-6 text-blue-600" />
              <h3 className="mt-4 font-display text-lg font-bold tracking-tight text-slate-900">
                {f.title}
              </h3>
              <p className="mt-2 text-base text-slate-600">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="pricing" className="bg-white py-16 lg:py-24" data-testid="landing-pricing">
        <div className="mx-auto max-w-6xl px-6">
          <div className="text-center">
            <div className="text-xs font-semibold uppercase tracking-[0.24em] text-blue-600">
              Pricing
            </div>
            <h2 className="mt-3 font-display text-3xl font-black tracking-tight text-slate-900 sm:text-4xl">
              Free to start. Upgrade when you grow.
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-base text-slate-600">
              Your customers and their trade references never pay a cent — only the
              Vendor/Supplier checking credit subscribes.
            </p>
          </div>

          <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            <div
              data-testid="landing-plan-free"
              className="ct-rise rounded-2xl border border-slate-200 bg-white p-8 shadow-sm"
            >
              <h3 className="font-display text-xl font-extrabold tracking-tight text-slate-900">
                Free Account
              </h3>
              <div className="mt-4 flex items-baseline gap-1.5">
                <span className="font-display text-5xl font-black tracking-tight text-slate-900">
                  $0
                </span>
                <span className="text-slate-500">/no expiration</span>
              </div>
              <p
                className="mt-3 text-sm font-normal italic text-slate-600"
                data-testid="landing-plan-tagline-free"
              >
                A great way to learn ConfirmTerms!
              </p>
              <ul className="mt-7 space-y-3">
                {[
                  "One user account",
                  "1 active credit application",
                  "10 archived* credit applications",
                  "Unlimited references",
                  "No expiration — keep it as long as you like",
                  "One-tap reference links",
                  "Branded credit packet PDF",
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2.5">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
                    <span className="text-sm text-slate-700">{f}</span>
                  </li>
                ))}
              </ul>
              <Link to="/signup">
                <Button
                  data-testid="landing-plan-cta-free"
                  className="mt-8 h-11 w-full rounded-full bg-slate-900 font-semibold transition-colors duration-200 hover:bg-slate-800 active:scale-95"
                >
                  Create a free account
                </Button>
              </Link>
            </div>

            {plans.map((p, i) => (
              <div
                key={p.lookup_key}
                data-testid={`landing-plan-${p.lookup_key}`}
                className={`ct-rise rounded-2xl border p-8 ${
                  i === plans.length - 1
                    ? "border-blue-600 bg-[#0d1b3e] shadow-lg"
                    : "border-slate-200 bg-white shadow-sm"
                }`}
              >
                <h3
                  className={`font-display text-xl font-extrabold tracking-tight ${
                    i === plans.length - 1 ? "text-white" : "text-slate-900"
                  }`}
                >
                  {p.name} Plan
                </h3>
                <div className="mt-4 flex items-baseline gap-1.5">
                  <span
                    className={`font-display text-5xl font-black tracking-tight ${
                      i === plans.length - 1 ? "text-white" : "text-slate-900"
                    }`}
                  >
                    {p.price}
                  </span>
                  <span
                    className={i === plans.length - 1 ? "text-slate-400" : "text-slate-500"}
                  >
                    /{p.interval}
                  </span>
                </div>
                {p.tax_note && (
                  <p
                    className={`mt-2 text-xs ${
                      i === plans.length - 1 ? "text-slate-400" : "text-slate-500"
                    }`}
                    data-testid={`landing-plan-tax-${p.lookup_key}`}
                  >
                    {p.tax_note}
                  </p>
                )}
                {p.tagline && (
                  <p
                    className={`mt-3 text-sm font-normal italic ${
                      i === plans.length - 1 ? "text-slate-300" : "text-slate-600"
                    }`}
                    data-testid={`landing-plan-tagline-${p.lookup_key}`}
                  >
                    {p.tagline}
                  </p>
                )}
                <ul className="mt-7 space-y-3">
                  {(p.features || []).map((f) => (
                    <li key={f} className="flex items-start gap-2.5">
                      <Check
                        className={`mt-0.5 h-4 w-4 shrink-0 ${
                          i === plans.length - 1 ? "text-blue-300" : "text-blue-600"
                        }`}
                      />
                      <span
                        className={`text-sm ${
                          i === plans.length - 1 ? "text-slate-200" : "text-slate-700"
                        }`}
                      >
                        {f}
                      </span>
                    </li>
                  ))}
                </ul>
                <Link to="/signup">
                  <Button
                    data-testid={`landing-plan-cta-${p.lookup_key}`}
                    className={`mt-8 h-11 w-full rounded-full font-semibold transition-colors duration-200 active:scale-95 ${
                      i === plans.length - 1
                        ? "bg-blue-600 hover:bg-blue-700"
                        : "bg-slate-900 hover:bg-slate-800"
                    }`}
                  >
                    Start free, upgrade anytime
                  </Button>
                </Link>
              </div>
            ))}
          </div>
          <p className="mt-10 text-center text-sm text-slate-500">
            Start on the free account — one user, 1 active credit application, 10 archived* credit
            applications and unlimited references, with no expiration. Move up to Starter, Small
            Business or Professional whenever you need more room.
          </p>
          <p
            className="mt-3 text-center text-xs text-slate-500"
            data-testid="landing-archive-footnote"
          >
            * - Archived credit applications may be purged by the subscribing company as needed
          </p>
          <p className="mt-1 text-center text-xs text-slate-500" data-testid="landing-tax-footnote">
            $15, $49 and $99 pricing is plus tax when applicable.
          </p>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-slate-50 py-10">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-6">
          <Brand />
          <p className="text-sm text-slate-500">
            © {new Date().getFullYear()} ConfirmTerms.com · Credit &amp; trade reference
            verification
          </p>
        </div>
      </footer>
    </div>
  );
}
